// ارسالِ دستور به دستگاه و نشان دادنِ نتیجهٔ واقعیِ آن
// سرور دستور را «sent» برمی‌گرداند و نتیجه را بعداً با رویدادِ سوکتِ commandResult می‌فرستد:
// success (دستگاه تأیید کرد)، failed (آفلاین بود یا رد کرد)، timeout (جواب نداد)،
// یا sent + deferred (دستگاه گفته بعداً اجرا می‌کند).
// اگر سوکت نرسید، هر چند ثانیه تاریخچهٔ دستورها خوانده می‌شود.
import Vue from 'vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import deviceService from '@/services/device.service'
import commandHistoryService from '@/services/command-history.service'

const POWER_COMMANDS = ['powerOn', 'powerOff']
const POLL_MS = 4000
// متنی که سرور برای «از قبل در همین حالت بود» می‌فرستد
const ALREADY_TEXT = 'از قبل در همین حالت بود'

function emptyPowerCmd() {
  return {
    phase: 'idle', // idle | sending | waiting | deferred | result
    imei: null,
    commandType: null,
    historyId: null,
    sentAt: 0,
    timeoutSec: 90,
    kind: '', // ok | bad | warn | timeout | wait | muted
    title: '',
    text: '',
    raw: '',
  }
}

// اعدادِ متن با رقمِ فارسی
export function faNum(value) {
  return String(value).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d])
}

function errorText(error) {
  const data = error && error.response && error.response.data
  if (data && data.message) return data.message
  return (error && error.message) || 'خطا در ارسالِ دستور'
}

export default {
  data() {
    return {
      powerCmd: emptyPowerCmd(),
      powerCmdNow: Date.now(),
      cmdOtherPending: {},
    }
  },
  mounted() {
    this.cmdEarlyResults = {}
    if (this.sockets && this.sockets.subscribe) {
      this.sockets.subscribe('commandResult', data => this.onCommandResult(data))
      this.sockets.subscribe('deviceStatus', data => this.onCommandDeviceStatus(data))
    }
  },
  beforeDestroy() {
    this.stopPowerCmdTimers()
    if (this.sockets && this.sockets.unsubscribe) {
      this.sockets.unsubscribe('commandResult')
      this.sockets.unsubscribe('deviceStatus')
    }
  },
  methods: {
    cmdDeviceInfo(imei) {
      const index = Vue.prototype.$functions.getDeviceIndex(imei)
      return (index !== undefined && index !== null && index >= 0) ? Vue.prototype.$devices[index] : null
    },
    // وضعیتِ رله از آخرین بستهٔ وضعیت: { known, cut }
    relayInfo(imei) {
      const info = this.cmdDeviceInfo(imei)
      const ti = info && info.serverStatus && info.serverStatus.terminalInfo
      if (!ti || typeof ti.relayState !== 'boolean') return { known: false, cut: false }
      return { known: true, cut: ti.relayState }
    },
    powerBtnDisabled(imei, commandType) {
      const s = this.powerCmd
      if (s.imei !== imei) return false
      if (s.phase === 'sending' || s.phase === 'waiting') return true
      return s.phase === 'deferred' && s.commandType === commandType
    },
    powerCmdElapsed() {
      if (!this.powerCmd.sentAt) return 0
      return Math.max(0, Math.round((this.powerCmdNow - this.powerCmd.sentAt) / 1000))
    },
    cmdToast(variant, title, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          text,
          icon: { success: 'CheckIcon', danger: 'XIcon' }[variant] || 'InfoIcon',
          variant,
          dir: Vue.prototype.$functions.currentLangDirection(),
        },
      })
    },

    // نقطهٔ ورود برای همهٔ دستورها
    sendDeviceCommand(imei, commandName) {
      if (POWER_COMMANDS.includes(commandName)) return this.sendPowerCommand(imei, commandName)
      return this.sendOtherCommand(imei, commandName)
    },

    async sendPowerCommand(imei, commandType) {
      if (this.powerBtnDisabled(imei, commandType)) return
      if (commandType === 'powerOff') {
        const info = this.cmdDeviceInfo(imei)
        const title = (info && info.title) || imei
        const ok = await this.$bvModal.msgBoxConfirm(
          'اگر خودرو در حرکت است، دستگاه تا وقتی سرعت کمتر از ۲۰ کیلومتر بر ساعت نشود قطع نمی‌کند. برای اجرا، GPS هم باید موقعیت گرفته باشد.',
          {
            title: `قطعِ سوخت و برقِ «${title}»؟`,
            okVariant: 'danger',
            okTitle: 'بله، قطع کن',
            cancelTitle: 'انصراف',
            cancelVariant: 'outline-secondary',
            centered: true,
            size: 'sm',
            bodyClass: 'text-right cmd-confirm-body',
            headerClass: 'cmd-confirm-header',
          },
        )
        if (!ok) return
      }

      this.stopPowerCmdTimers()
      this.powerCmd = {
        ...emptyPowerCmd(),
        phase: 'sending',
        imei,
        commandType,
      }

      let res
      try {
        res = await deviceService.sendCommand({ imei, commandName: commandType })
      } catch (error) {
        this.setPowerResult('bad', 'دستور فرستاده نشد', errorText(error))
        return
      }

      const r = (res && res.result) || {}
      if (res && res.status === 'success') {
        if (typeof this.onCommandSent === 'function') {
          this.onCommandSent({ command: commandType, success: true, response: r })
        }
        if (!r.historyId) {
          // سرورِ قدیمی: فقط ارسال
          this.setPowerResult('ok', 'دستور فرستاده شد', '')
          return
        }
        this.powerCmd = {
          ...this.powerCmd,
          phase: 'waiting',
          historyId: r.historyId,
          sentAt: Date.now(),
          timeoutSec: r.timeoutSec || 90,
        }
        this.updateRelayState(imei, r.relayState)
        this.startPowerCmdTimers()
        const early = this.cmdEarlyResults[r.historyId]
        if (early) this.applyPowerResult(early)
        return
      }

      if (r.code === 'BUSY') {
        this.powerCmd = {
          ...this.powerCmd,
          phase: 'result',
          kind: 'wait',
          title: 'دستورِ قبلی هنوز منتظرِ تأیید است',
          text: `${faNum(r.retryAfterSec || 90)} ثانیه دیگر می‌توانید دستورِ تازه بدهید.`,
        }
      } else if (r.code === 'DEVICE_OFFLINE') {
        this.setPowerResult('bad', 'دستگاه الان به سرور وصل نیست', 'دستور فرستاده نشد. وقتی دستگاه دوباره وصل شد، امتحان کنید.')
      } else {
        this.setPowerResult('bad', 'دستور فرستاده نشد', (res && res.message) || '')
      }
    },

    async sendOtherCommand(imei, commandName) {
      let res
      try {
        res = await deviceService.sendCommand({ imei, commandName })
      } catch (error) {
        this.cmdToast('danger', 'خطا', errorText(error))
        return
      }
      const r = (res && res.result) || {}
      if (res && res.status === 'success') {
        if (typeof this.onCommandSent === 'function') {
          this.onCommandSent({ command: commandName, success: true, response: r })
        }
        if (r.historyId) {
          this.$set(this.cmdOtherPending, r.historyId, { imei, commandName })
          this.cmdToast('info', 'دستور به دستگاه رسید', 'منتظرِ تأییدِ دستگاه… نتیجه همین‌جا و در تاریخچهٔ دستورها نشان داده می‌شود.')
          const early = this.cmdEarlyResults[r.historyId]
          if (early) this.applyOtherResult(early)
        } else {
          this.cmdToast('success', this.$t('Message'), 'دستور با موفقیت ارسال شد.')
        }
        return
      }
      this.cmdToast('danger', 'دستور فرستاده نشد', (res && res.message) || 'خطا در ارسالِ دستور')
    },

    onCommandResult(data) {
      if (!data || !data.historyId) return
      this.updateRelayState(data.imei, data.relayState)
      if (this.powerCmd.historyId === data.historyId) {
        this.applyPowerResult(data)
      } else if (this.cmdOtherPending[data.historyId]) {
        this.applyOtherResult(data)
      } else {
        // ممکن است نتیجه پیش از جوابِ HTTP برسد؛ تا یک دقیقه نگه داشته می‌شود
        this.cmdEarlyResults[data.historyId] = data
        setTimeout(() => { delete this.cmdEarlyResults[data.historyId] }, 60000)
      }
      this.$forceUpdate()
    },

    onCommandDeviceStatus(data) {
      if (data && data.imei && data.imei === this.powerCmd.imei) this.$forceUpdate()
    },

    updateRelayState(imei, relayState) {
      if (typeof relayState !== 'boolean') return
      const info = this.cmdDeviceInfo(imei)
      if (info && info.serverStatus && info.serverStatus.terminalInfo) {
        info.serverStatus.terminalInfo.relayState = relayState
      }
    },

    applyPowerResult(data) {
      const type = this.powerCmd.commandType
      const verb = type === 'powerOff' ? 'قطع' : 'وصل'
      const sec = Math.max(1, Math.round((data.elapsedMs || 0) / 1000))
      if (data.status === 'sent' && data.deferred) {
        this.stopPowerCmdTimers()
        this.powerCmd = {
          ...this.powerCmd,
          phase: 'deferred',
          kind: 'warn',
          title: `${verb} در صفِ دستگاه است`,
          text: `${data.replyText || ''}${type === 'powerOff' ? ' برای لغو، «وصل» را بزنید.' : ''}`,
          raw: data.deviceReply || '',
        }
        return
      }
      if (data.status === 'success') {
        const already = data.replyText && data.replyText.indexOf(ALREADY_TEXT) >= 0
        this.setPowerResult('ok', `${verb} شد`,
          already ? 'رله از قبل در همین حالت بود.' : `دستگاه در ${faNum(sec)} ثانیه تأیید کرد.`, data.deviceReply)
      } else if (data.status === 'failed' && data.reason === 'SUPERSEDED') {
        this.setPowerResult('muted', `${verb} لغو شد`, 'با دستورِ بعدی جایگزین شد.')
      } else if (data.status === 'failed') {
        this.setPowerResult('bad', `دستگاه ${verb} را اجرا نکرد`, data.replyText || '', data.deviceReply)
      } else if (data.status === 'timeout') {
        const relay = this.relayInfo(this.powerCmd.imei)
        let text = 'اگر دوباره هم نشد، با پشتیبانی تماس بگیرید.'
        if (relay.known) {
          const done = type === 'powerOff' ? relay.cut : !relay.cut
          text = `وضعیتِ فعلیِ رله «${relay.cut ? 'قطع' : 'وصل'}» است، یعنی ${verb} ${done ? 'انجام شده' : 'انجام نشده'}. ${text}`
        }
        this.setPowerResult('timeout', data.deferred ? `دستگاه تا ۳۰ دقیقه ${verb} را اجرا نکرد` : 'دستگاه جواب نداد', text)
      }
    },

    applyOtherResult(data) {
      this.$delete(this.cmdOtherPending, data.historyId)
      if (data.status === 'success') {
        this.cmdToast('success', 'دستگاه دستور را تأیید کرد', data.replyText || data.deviceReply || 'انجام شد')
      } else if (data.status === 'failed') {
        this.cmdToast('danger', 'دستگاه دستور را اجرا نکرد', data.replyText || data.deviceReply || '')
      } else if (data.status === 'timeout') {
        this.cmdToast('warning', 'دستگاه جواب نداد', 'نتیجهٔ دستور معلوم نیست. در تاریخچهٔ دستورها ببینید.')
      } else if (data.status === 'sent' && data.deferred) {
        this.cmdToast('warning', 'دستور در صفِ دستگاه است', data.replyText || '')
      }
    },

    dismissPowerCmd() {
      this.stopPowerCmdTimers()
      this.powerCmd = emptyPowerCmd()
    },

    setPowerResult(kind, title, text, raw) {
      this.stopPowerCmdTimers()
      this.powerCmd = {
        ...this.powerCmd,
        phase: 'result',
        kind,
        title,
        text,
        raw: raw || '',
      }
    },

    startPowerCmdTimers() {
      this.powerCmdNow = Date.now()
      this.powerCmdTicker = setInterval(() => { this.powerCmdNow = Date.now() }, 1000)
      // پشتیبان: اگر رویدادِ سوکت نرسید، از تاریخچه بخوان
      this.powerCmdPoller = setInterval(() => this.pollPowerCmd(), POLL_MS)
    },

    stopPowerCmdTimers() {
      clearInterval(this.powerCmdTicker)
      clearInterval(this.powerCmdPoller)
      this.powerCmdTicker = null
      this.powerCmdPoller = null
    },

    async pollPowerCmd() {
      const s = this.powerCmd
      if (s.phase !== 'waiting' || !s.historyId) return
      try {
        const res = await commandHistoryService.getHistory({ imei: s.imei, limit: 10 })
        const list = (res && res.result && res.result.data) || []
        // eslint-disable-next-line no-underscore-dangle
        const rec = list.find(item => String(item._id || item.id) === String(s.historyId))
        if (!rec || this.powerCmd.historyId !== s.historyId || this.powerCmd.phase !== 'waiting') return
        this.updateRelayState(s.imei, rec.relayAfter)
        if (rec.status === 'sent' && !rec.deferred) {
          // اگر سرور بیش از ۳۰ ثانیه از مهلت عقب بود، دیگر منتظر نمانیم
          if (Date.now() - s.sentAt > (s.timeoutSec + 30) * 1000) {
            this.applyPowerResult({ historyId: s.historyId, status: 'timeout' })
          }
          return
        }
        this.applyPowerResult({
          historyId: s.historyId,
          status: rec.status,
          reason: rec.reason,
          deferred: rec.deferred,
          replyText: rec.replyText,
          deviceReply: rec.deviceReply,
          elapsedMs: rec.confirmedAt && rec.sentAt ? new Date(rec.confirmedAt) - new Date(rec.sentAt) : Date.now() - s.sentAt,
        })
      } catch (e) {
        // شبکه: دورِ بعد
      }
    },
  },
}
